import type {
  ExecutionEnvironmentDescriptor,
  FirstAccountSetupState,
  FirstAccountSetupStepId,
} from "@cmmd-forge/contracts";
import { normalizeExecutionEnvironmentCapabilities } from "@cmmd-forge/client-runtime";

import { firstRunEnvironmentEntry } from "./firstRunEnvironment";

export const FIRST_ACCOUNT_SETUP_STORAGE_KEY = "forge:first-account-setup:v2";

export type { FirstAccountSetupState, FirstAccountSetupStepId };

export const DEFAULT_FIRST_ACCOUNT_SETUP_STATE: FirstAccountSetupState = {
  status: "pending",
  activeStepId: "account",
};

/**
 * The terminal setup state, shared by the first-account-setup page and the
 * welcome wizard so "setup complete" means the same thing no matter which
 * surface wrote it. The wizard writes this on finish so a fresh browser that
 * chose its first project in the wizard is not bounced through the setup page
 * afterwards.
 */
export const COMPLETED_FIRST_ACCOUNT_SETUP_STATE: FirstAccountSetupState = {
  status: "completed",
  activeStepId: "first-run",
};

function isPersistedSetupStepId(value: unknown): value is FirstAccountSetupStepId {
  return (
    value === "account" ||
    value === "usage" ||
    value === "providers" ||
    value === "projects" ||
    value === "capabilities" ||
    value === "first-run"
  );
}

function normalizeSetupStepId(
  stepId: FirstAccountSetupStepId | "environment",
): FirstAccountSetupStepId {
  if (stepId === "providers") {
    return "usage";
  }
  if (stepId === "environment") {
    return "account";
  }
  return stepId;
}

export function readFirstAccountSetupState(storage: Storage | undefined): FirstAccountSetupState {
  if (!storage) {
    return DEFAULT_FIRST_ACCOUNT_SETUP_STATE;
  }

  try {
    const value = storage.getItem(FIRST_ACCOUNT_SETUP_STORAGE_KEY);
    if (value === "completed") {
      return { status: "completed", activeStepId: "first-run" };
    }
    if (value === "skipped" || value === "pending" || value === null) {
      return DEFAULT_FIRST_ACCOUNT_SETUP_STATE;
    }

    const parsed = JSON.parse(value) as {
      readonly status?: unknown;
      readonly activeStepId?: unknown;
    };
    if (
      (parsed.status === "pending" ||
        parsed.status === "in_progress" ||
        parsed.status === "completed") &&
      (isPersistedSetupStepId(parsed.activeStepId) || parsed.activeStepId === "environment")
    ) {
      return {
        status: parsed.status,
        activeStepId: normalizeSetupStepId(parsed.activeStepId),
      };
    }
  } catch {
    return DEFAULT_FIRST_ACCOUNT_SETUP_STATE;
  }

  return DEFAULT_FIRST_ACCOUNT_SETUP_STATE;
}

export function writeFirstAccountSetupState(
  storage: Storage | undefined,
  state: FirstAccountSetupState,
): void {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(FIRST_ACCOUNT_SETUP_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage can be unavailable in restricted browser modes. The app should still render.
  }
}

export function resolveFirstAccountSetupState(input: {
  readonly useCmmdState: boolean;
  readonly cmmdState: FirstAccountSetupState | null;
  readonly serverState: FirstAccountSetupState;
}): FirstAccountSetupState | null {
  if (input.useCmmdState) {
    return input.cmmdState;
  }
  return input.serverState;
}

export function shouldUseCmmdFirstAccountSetupState(
  descriptor: ExecutionEnvironmentDescriptor,
): boolean {
  const runtime = normalizeExecutionEnvironmentCapabilities(descriptor).runtime;
  return runtime.workspaceProxy || runtime.topology === "cloud-shell";
}

/**
 * Whether a surface should route personal sign-in through the cloud auth VM.
 *
 * Two conditions, both required: the build flag is on, AND this surface has no
 * execution plane of its own. An execution-plane surface (desktop, local web, a
 * user runtime) can run `claude auth login` locally, and hijacking it into
 * provisioning a throwaway auth VM would both cost a VM and bypass the working
 * local path.
 *
 * Shared by Settings and onboarding deliberately. Onboarding had this guard and
 * Settings did not, which was harmless only while the Claude flag was off
 * everywhere; turning it on for the cloud-shell build makes the divergence
 * reachable. One definition, so the two surfaces cannot disagree again.
 */
export function resolveCloudSignInActive(input: {
  readonly descriptor: ExecutionEnvironmentDescriptor | null | undefined;
  readonly enabled: boolean;
}): boolean {
  if (!input.enabled) {
    return false;
  }
  // No descriptor yet: assume we DO have an execution plane and stay off. The
  // safe default is the local path, which is merely unavailable, rather than
  // spending a VM on a surface that never needed one.
  const executionPlane = input.descriptor
    ? normalizeExecutionEnvironmentCapabilities(input.descriptor).runtime.executionPlane
    : true;
  return !executionPlane;
}

export function shouldShowFirstAccountSetup(input: {
  readonly isAuthenticated: boolean;
  readonly pathname: string;
  readonly state: FirstAccountSetupState;
  readonly hasSeat?: boolean;
  readonly hasEnvironmentId?: boolean;
}): boolean {
  if (!input.isAuthenticated) {
    return false;
  }

  if (input.pathname === "/login" || input.pathname === "/pair" || input.pathname === "/setup") {
    return false;
  }

  const entry = firstRunEnvironmentEntry({
    hasSeat: input.hasSeat ?? true,
    hasEnvironmentId: input.hasEnvironmentId ?? true,
    setupCompleted: input.state.status === "completed",
  });
  if (entry.kind !== "continue") return true;
  return input.state.status !== "completed";
}
